import { useState } from 'react';

import { DisplayComponent } from '@/components/DisplayComponent';
import { DisplayHeading } from '@/components/DisplayHeading';
import { Header } from '@/components/Header';
import { ProgressBar } from '@/components/ProgressBar';
import { selectBillsPaidFrequency } from '@/context/borrower';
import { selectSkipExpenseScreen } from '@/context/display';
import { store } from '@/context/store';
import { useAppSelector } from '@/context/storeHooks';
import { useScreenCopy } from '@/copy';
import { AmountPaid } from '@/UserInput/AmountPaid';
import { EmploymentStatus } from '@/UserInput/EmploymentStatus';
import { HecsDebt } from '@/UserInput/HecsDebt';
import { HousingFrequency } from '@/UserInput/HousingFrequency';
import { MortgageRent } from '@/UserInput/MortgageRent';
import { OtherIncome } from '@/UserInput/OtherIncome';
import { PaidFrequency } from '@/UserInput/PaidFrequency';

import { IncomeButtons } from './IncomeButtons';

export const testId = 'Income';

function Income(): JSX.Element {
    const baseId = 'screen-income';
    const billsPaidFrequency = useAppSelector(selectBillsPaidFrequency);
    const screenCopy = useScreenCopy('income');
    const skipExpenseScreen = useAppSelector(selectSkipExpenseScreen);
    // ***** Local State *****
    const [showErrors, setShowErrors] = useState(false);

    const income = useAppSelector((state) => state.borrowerReducer.income);
    const state = store.getState();
    const isBrandNovated = state.displayReducer.partner.isNovatedLease;

    const isHecsDebtDisplayed = (): boolean => {
        if (!income) return false;

        let annualIncome = 0;
        // Convert paid amount to annual based on frequency
        if (income.paidAmount) {
            switch (income.paidFrequency) {
                case 'WEEKLY':
                    annualIncome =
                        (income.paidAmount + (income.otherIncome || 0)) * 52;
                    break;
                case 'FORTNIGHTLY':
                    annualIncome =
                        (income.paidAmount + (income.otherIncome || 0)) * 26;
                    break;
                case 'MONTHLY':
                    annualIncome =
                        (income.paidAmount + (income.otherIncome || 0)) * 12;
                    break;
                default:
                    annualIncome =
                        income.paidAmount + (income.otherIncome || 0);
            }
        }

        return annualIncome < 65000;
    };
    const isNovatedLease = state.borrowerReducer.loan.loanUsage === 'NOVATED';
    const amountPaid = useAppSelector(
        (state) => state.borrowerReducer.income.paidAmount
    );

    return (
        <div data-testid={testId}>
            <DisplayComponent baseId={baseId}>
                <Header />
                <ProgressBar />
                <DisplayHeading
                    title={screenCopy.title}
                    infoText={screenCopy.subtitle}
                />
                <EmploymentStatus showErrors={showErrors} baseId={baseId} />
                <PaidFrequency showErrors={showErrors} baseId={baseId} />
                <AmountPaid showErrors={showErrors} baseId={baseId} />
                <OtherIncome showErrors={showErrors} baseId={baseId} />
                {skipExpenseScreen && (
                    <>
                        {/* The pay cycle used to label this field, while the
                            serviceability call converted it as monthly. Asked
                            directly now, so the label and the calculation
                            agree and nobody has to convert anything. */}
                        <HousingFrequency
                            showErrors={showErrors}
                            baseId={baseId}
                        />
                        <MortgageRent
                            showErrors={showErrors}
                            baseId={baseId}
                            frequency={billsPaidFrequency}
                        />
                    </>
                )}
                {isBrandNovated &&
                    isNovatedLease &&
                    isHecsDebtDisplayed() &&
                    amountPaid !== undefined && (
                        <HecsDebt showErrors={showErrors} baseId={baseId} />
                    )}
                <IncomeButtons setShowErrors={setShowErrors} baseId={baseId} />
            </DisplayComponent>
        </div>
    );
}

export { Income };
